/* eslint-disable import/no-named-as-default */
import { useRouter } from 'next/router';
import { useContext, useMemo } from 'react';
import Flex from './Flex';
import TextButton from './TextButton';
import { ROLE_VALUE } from '@/constants';
import AuthContext from '@/contexts/auth';
import SchoolContext from '@/contexts/school';
import SettingsContext from '@/contexts/settings';
import useCurrentUser from '@/hooks/useCurrentUser';
import * as styles from '@/styles/components/HomeList';

const schoolHome = '/[schoolSlug]/schedule';
const tournamentHome = '/[schoolSlug]/tournament-list';

const HomeList = () => {
  const { schoolName } = useContext(SettingsContext);

  const { is_school_contract, is_tournament_contract, is_rental_contract } = useContext(SchoolContext);

  const { currentUser } = useContext(AuthContext);

  const { data: adminAccount, checkAccess } = useCurrentUser(currentUser?.uid);

  /**
   * Check if can access to school
   */
  const schoolPermission = useMemo(() => {
    return is_school_contract && checkAccess(schoolHome);
  }, [is_school_contract, adminAccount]);

  /**
   * Check if can access to tournament
   */
  const tournamentPermission = useMemo(() => {
    return is_tournament_contract && checkAccess(tournamentHome);
  }, [is_tournament_contract, adminAccount]);

  /**
   * Check if can access to court rental
   */
  const courtRentalPermission = useMemo(() => {
    return is_rental_contract && adminAccount?.user?.court_rental_role;
  }, [is_rental_contract, adminAccount]);

  return (
    <div style={{ paddingTop: '10px' }}>
      <div className='uk-margin-small-bottom uk-text-bold'>{schoolName || ''}</div>
      <div css={styles.gridLayout}>
        {schoolPermission ? (
          <SchoolTag isDisabled={false} adminRole={adminAccount?.user?.school_role || 0} />
        ) : adminAccount?.user?.tournament_role === ROLE_VALUE.ADMIN && !is_school_contract ? (
          <SchoolTag isDisabled={true} adminRole={0} />
        ) : (
          <></>
        )}
        {tournamentPermission ? (
          <TournamentTag isDisabled={false} />
        ) : adminAccount?.user?.school_role === ROLE_VALUE.ADMIN && !is_tournament_contract ? (
          <TournamentTag isDisabled={true} />
        ) : (
          <></>
        )}
        {/* Disable rental feature */}
        {/* {courtRentalPermission ? (
          <CourtRentalTag isDisabled={false} />
        ) : adminAccount?.user?.school_role === ROLE_VALUE.ADMIN && !is_rental_contract ? (
          <CourtRentalTag isDisabled={true} />
        ) : (
          <></>
        )} */}
        {courtRentalPermission ? <CourtRentalTag isDisabled={false} /> : <></>}
      </div>
    </div>
  );
};

const SchoolTag = ({ isDisabled, adminRole }: { isDisabled: boolean; adminRole: number }) => {
  const { slug } = useContext(SchoolContext);
  const router = useRouter();

  /**
   * Handle redirect to school
   */
  const redirectToSchool = () => {
    switch (adminRole) {
      case ROLE_VALUE.ADMIN:
        router.push(`/${slug}/dashboard`);
        break;

      case ROLE_VALUE.STAFF:
      case ROLE_VALUE.PARTNER:
        router.push(`/${slug}/schedule`);
        break;

      default:
        break;
    }
  };

  return (
    <div css={styles.rootList(isDisabled)} onClick={() => redirectToSchool()}>
      <Flex gap={16} alignItems='center'>
        <div style={{ flexShrink: 0 }} css={styles.image('"/images/school.png"')} />
        <Flex type='column' gap={8}>
          <Flex justifyContent='space-between' alignItems='center'>
            <Flex gap={10} alignItems='center'>
              <TextButton color={'#303033'} underline={false} fontSize='16px'>
                スクール・レッスン
              </TextButton>
              {isDisabled && <div css={styles.tag}>未連携</div>}
            </Flex>
            {isDisabled && (
              <div
                onClick={() => {
                  window.open('https://official.spohabi.com/contact/', '_blank');
                }}
                style={{ marginTop: '-5px' }}>
                <TextButton fontSize='12px' color={'#2D8F8E'} underline={true}>
                  ご利用のお問合せはこちら
                </TextButton>
              </div>
            )}
          </Flex>

          <div style={{ fontSize: '14px' }}>
            会員の管理やレッスンスケジュール、参加者の管理、イベントの企画・実施など、一元的にスクールを管理し、効率的な運営をサポートします。
          </div>
        </Flex>
      </Flex>
    </div>
  );
};

const TournamentTag = ({ isDisabled }: { isDisabled: boolean }) => {
  const { slug } = useContext(SchoolContext);
  const router = useRouter();

  /**
   * Handle redirect to tournament
   */
  const redirectToTournament = () => {
    !isDisabled && router.push(`/${slug}/tournament-list`);
  };

  return (
    <div css={styles.rootList(isDisabled)} onClick={() => redirectToTournament()}>
      <Flex alignItems='center' gap={16}>
        <div style={{ flexShrink: 0 }} css={styles.image('"/images/tournament.png"')} />
        <Flex type='column' gap={8}>
          <Flex justifyContent='space-between' alignItems='center'>
            <Flex gap={10} alignItems='center'>
              <TextButton color={'#303033'} underline={false} fontSize='16px'>
                大会
              </TextButton>
              {isDisabled && <div css={styles.tag}>未連携</div>}
            </Flex>
            {isDisabled && (
              <div
                onClick={() => {
                  window.open('https://official.spohabi.com/contact/', '_blank');
                }}
                style={{ marginTop: '-5px' }}>
                <TextButton fontSize='12px' color={'#2D8F8E'} underline={true}>
                  ご利用のお問合せはこちら
                </TextButton>
              </div>
            )}
          </Flex>
          <div style={{ fontSize: '14px' }}>
            集客ページや参加者、対戦表やスコアなど大会運用に必要な機能を備えています。また、参加者へリアルタイムで最新情報を共有。大会運営の手間を削減し、スムーズな大会進行を実現します。
          </div>
        </Flex>
      </Flex>
    </div>
  );
};

const CourtRentalTag = ({ isDisabled }: { isDisabled: boolean }) => {
  const { slug } = useContext(SchoolContext);
  const router = useRouter();

  /**
   * Handle redirect to court rental
   */
  const redirectToCourtRental = () => {
    !isDisabled && router.push(`/${slug}/rental/facility-list`);
  };

  return (
    <div css={styles.rootList(isDisabled)} onClick={() => redirectToCourtRental()}>
      <Flex alignItems='center' gap={16}>
        <div style={{ flexShrink: 0 }} css={styles.image('"/images/court_rental.png"')} />
        <Flex type='column' gap={8}>
          <Flex justifyContent='space-between' alignItems='center'>
            <Flex gap={10} alignItems='center'>
              <TextButton color={'#303033'} underline={false} fontSize='16px'>
                レンタル施設
              </TextButton>
              {isDisabled && <div css={styles.tag}>未連携</div>}
            </Flex>
            {isDisabled && (
              <div
                onClick={() => {
                  window.open('https://official.spohabi.com/contact/', '_blank');
                }}
                style={{ marginTop: '-5px' }}>
                <TextButton fontSize='12px' color={'#2D8F8E'} underline={true}>
                  ご利用のお問合せはこちら
                </TextButton>
              </div>
            )}
          </Flex>
          <div style={{ fontSize: '14px' }}>
            集客ページ、施設の料金設定、予約スケジュール、予約者の管理など、一元的に情報を管理し、スムーズなレンタル施設の運営を実現します。
          </div>
        </Flex>
      </Flex>
    </div>
  );
};

export default HomeList;
